import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSpares, useUpdateSpare, type SpareRow, type Spares } from "../../api/admin";
import { formatMoney } from "../../api/money";
import { StatusChip } from "../../components/ui";
import { matches } from "./AdminDrawer";
import AdminLayout from "./AdminLayout";

/** The spares catalogue: what each part costs, what it sells for, which
 * supplier account it is bought from and under which published code. A code
 * is chosen from the supplier's own list; choosing one with no prices typed
 * loads the list's. */
export default function SparesPage() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const supplier = params.get("supplier_id");
  const view = useSpares(supplier === null ? null : Number(supplier));
  const data = view.data;
  const rows = (data?.rows ?? []).filter((row) =>
    matches(query, [row.part_description, row.supplier_item_code, row.size, row.supplier, row.supplier_name]),
  );
  return (
    <AdminLayout
      title="Spares"
      sub={
        data
          ? `${data.rows.length} parts · ${data.counts.needing_code} need a code · ${data.counts.unpriced} unpriced · ${data.counts.priced_from_list} priced from a list`
          : "What each part costs, what it sells for, and who supplies it"
      }
    >
      <div className="toolbar">
        <input
          type="search"
          className="search-input admin-search"
          placeholder="Search code or description"
          aria-label="Search code or description"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {data ? (
          <select
            className="select"
            aria-label="Supplier"
            value={supplier ?? ""}
            onChange={(e) => setParams(e.target.value ? { supplier_id: e.target.value } : {})}
          >
            <option value="">Every supplier</option>
            {data.suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        ) : null}
      </div>
      {data && data.lists_unread.length > 0 ? (
        <p className="notice warn">Price list not read for {data.lists_unread.join(", ")}.</p>
      ) : null}
      {view.isError ? <p className="notice bad">The catalogue could not be loaded: {view.error.message}</p> : null}
      {view.isPending ? <div className="table-card empty-state">Loading the catalogue…</div> : null}
      {data ? (
        <div className="table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Part</th>
                <th>Catalogue</th>
                <th>Account</th>
                <th className="num">Cost</th>
                <th className="num">Selling</th>
                <th className="num">Markup</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <SpareRowView key={row.id} row={row} data={data} />
              ))}
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="empty-state">
                    {data.rows.length === 0 ? "No parts in the catalogue for this supplier." : `No part matches “${query}”.`}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}
    </AdminLayout>
  );
}

function SpareRowView({ row, data }: { row: SpareRow; data: Spares }) {
  const update = useUpdateSpare();
  const [open, setOpen] = useState(false);
  const [supplier, setSupplier] = useState(row.supplier_id === null ? data.no_supplier : String(row.supplier_id));
  const [code, setCode] = useState(row.supplier_item_code ?? "");
  const [cost, setCost] = useState(row.unit_cost ?? "");
  const [selling, setSelling] = useState(row.selling_price ?? "");
  const codes = supplier === data.no_supplier ? [] : data.codes_by_supplier[supplier] ?? [];
  return (
    <>
      <tr className={open ? "selected" : undefined}>
        <td className="mono">
          {row.supplier_item_code ?? "—"}
          {row.needs_code ? (
            <>
              {" "}
              <StatusChip tone="warn">needs a code</StatusChip>
            </>
          ) : null}
        </td>
        <td>
          {row.part_description}
          {row.priced_from_list ? <span className="sub">priced from the list</span> : null}
        </td>
        <td className="muted">
          {row.supplier} · {row.size}
        </td>
        <td>{row.supplier_name ?? <span className="muted">none</span>}</td>
        <td className="num">{row.unit_cost ? formatMoney(row.unit_cost) : "—"}</td>
        <td className="num">{row.selling_price ? formatMoney(row.selling_price) : "—"}</td>
        <td className="num">{row.markup ? formatMoney(row.markup) : "—"}</td>
        <td className="num">
          <button type="button" className="row-button" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
            {open ? "Close" : "Edit"}
          </button>
        </td>
      </tr>
      {open ? (
        <tr className="manage-row">
          <td colSpan={8}>
            <form
              className="manage-grid"
              onSubmit={(e) => {
                e.preventDefault();
                update.mutate({ id: row.id, body: { supplier_item_code: code.trim() || null, unit_cost: cost.trim() || null, selling_price: selling.trim() || null, supplier_id: supplier } }, { onSuccess: () => setOpen(false) });
              }}
            >
              <label className="field">
                Account
                <select value={supplier} onChange={(e) => setSupplier(e.target.value)}>
                  <option value={data.no_supplier}>No supplier</option>
                  {data.suppliers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                Code
                {codes.length > 0 ? (
                  <select
                    value={codes.some((c) => c.code === code) ? code : ""}
                    onChange={(e) => {
                      setCode(e.target.value);
                      const found = codes.find((c) => c.code === e.target.value);
                      if (found) {
                        setCost(found.cost ?? "");
                        setSelling(found.selling ?? "");
                      }
                    }}
                  >
                    <option value="">{code ? `${code} (not on the list)` : "Choose from the list…"}</option>
                    {codes.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input value={code} onChange={(e) => setCode(e.target.value)} />
                )}
              </label>
              <label className="field">
                Cost
                <input inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="blank clears" />
              </label>
              <label className="field">
                Selling
                <input inputMode="decimal" value={selling} onChange={(e) => setSelling(e.target.value)} placeholder="blank clears" />
              </label>
              <button type="submit" className="btn btn-primary" disabled={update.isPending}>
                Save
              </button>
              {update.isError ? <span className="notice bad">{update.error.message}</span> : null}
            </form>
          </td>
        </tr>
      ) : null}
    </>
  );
}
